import type { Locale } from './domain.js';

/**
 * Grouping a history into the periods somebody actually remembers it in.
 *
 * A customer's record is not evenly spread. It arrives in bursts: a call, a quote, two visits
 * and an invoice inside three weeks, then eleven months of silence, then another burst. Listed
 * flat, three years of that is a wall of entries where the shape — *we did a lot for these
 * people in spring 2025 and have not heard from them since* — is invisible.
 *
 * So periods are found in the data rather than imposed on it. Fixed calendar buckets would cut
 * a single week of work in half whenever it crossed the end of a month, and a job that begins
 * on 28 December is one episode, not two.
 */

/** Silence long enough to mean the last thing finished and the next has not started. */
const DEFAULT_GAP_DAYS = 45;

export type Period<T> = {
  /** The compact label — `avr. – juin 2025`. */
  label: string;
  start: Date;
  end: Date;
  items: T[];
};

/**
 * Split entries into bursts of activity, newest first.
 *
 * Entries may arrive in any order; they are sorted here. Anything without a usable date is
 * dropped from the grouping rather than silently bucketed into "now", which would put a record
 * with a broken timestamp at the top of somebody's history.
 */
export function groupIntoPeriods<T>(
  items: readonly T[],
  getDate: (item: T) => string | Date | null | undefined,
  locale: Locale,
  gapDays: number = DEFAULT_GAP_DAYS,
): Array<Period<T>> {
  const dated = items
    .map((item) => ({ item, at: toDate(getDate(item)) }))
    .filter((entry): entry is { item: T; at: Date } => entry.at !== null)
    .sort((a, b) => b.at.getTime() - a.at.getTime());

  if (dated.length === 0) return [];

  const gap = gapDays * 24 * 60 * 60 * 1000;
  const groups: Array<{ items: T[]; start: Date; end: Date }> = [];
  let current = { items: [dated[0]!.item], start: dated[0]!.at, end: dated[0]!.at };

  for (const entry of dated.slice(1)) {
    // Walking backwards, so `current.start` is the oldest entry seen in this burst so far.
    if (current.start.getTime() - entry.at.getTime() > gap) {
      groups.push(current);
      current = { items: [entry.item], start: entry.at, end: entry.at };
    } else {
      current.items.push(entry.item);
      current.start = entry.at;
    }
  }
  groups.push(current);

  return groups.map((group) => ({ ...group, label: formatSpan(group.start, group.end, locale) }));
}

/**
 * The shortest label that still names real months.
 *
 * The year is stated once, at the end, because a range is read left to right and the reader
 * only needs anchoring at the point they stop. A period crossing New Year takes the year it
 * ended in — `déc. – janv. 2026` is the winter that finished in 2026, which is how people
 * refer to it out loud. The exact dates belong in a tooltip; this is a heading, and a heading
 * that wraps has failed at being one.
 */
export function formatSpan(start: Date, end: Date, locale: Locale): string {
  const tag = locale === 'en' ? 'en-CA' : 'fr-CA';
  const month = (date: Date) => new Intl.DateTimeFormat(tag, { month: 'short' }).format(date);
  const year = end.getFullYear();

  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
  if (sameMonth) {
    // A single month is named in full: there is room, and "août 2026" reads better than "août".
    return `${new Intl.DateTimeFormat(tag, { month: 'long' }).format(end)} ${year}`;
  }

  return `${month(start)} – ${month(end)} ${year}`;
}

/** The full span, for the tooltip that sits behind the short label. */
export function formatSpanExact(start: Date, end: Date, locale: Locale): string {
  const tag = locale === 'en' ? 'en-CA' : 'fr-CA';
  const format = new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'long', year: 'numeric' });
  const from = format.format(start);
  const to = format.format(end);
  return from === to ? from : `${from} – ${to}`;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
